import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { AppLayout } from '../components/layout/AppLayout'
import { PageContainer } from '../components/layout/PageContainer'
import { Box, Button } from '../components/ui/foundation'
import {
  inlineLinkClass,
  kickerLabelClass,
  secondaryCardClass,
  secondaryHelperTextClass,
  secondaryLabelClass,
  secondaryPageTitleClass,
  secondaryTextInputClass,
} from '../components/secondary-pages/styles'
import { useUserProfile } from '../hooks/useUserProfile'
import { useAuth } from '../hooks/useAuth'
import { showToast } from '../utils/toast'
import { logError } from '../utils/logger'
import { ToastContainer } from '../components/ui/composite/ToastContainer'
import { formatPhoneNumber, validatePhoneNumber } from '../utils/phoneUtils'
import { Check } from '@phosphor-icons/react'
import type { User } from '@supabase/supabase-js'
import { MetaTags } from '../components/seo/MetaTags'

type UseUserProfileReturn = ReturnType<typeof useUserProfile>

type MarketingPreferences = Record<string, unknown> & {
  marketing_consent?: boolean
}

const getMarketingConsent = (preferences?: Record<string, unknown>): boolean =>
  (preferences as MarketingPreferences | undefined)?.marketing_consent ?? false

const accountLegalLinkClass =
  'rounded font-sans text-fs-300 text-ink-500 transition-colors duration-touch ease-precise hover:text-primary-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white'

const marketingConsentControlClass = 'peer sr-only'

const marketingConsentBoxClass =
  'mt-0.5 flex h-[13px] w-[13px] shrink-0 items-center justify-center rounded-[3px] border border-[rgba(36,34,30,0.12)] bg-white text-white transition-[background-color,border-color] duration-150 ease-precise group-hover:border-[rgba(36,34,30,0.2)] [&_svg]:opacity-0 [&_svg]:transition-opacity [&_svg]:duration-touch [&_svg]:ease-precise peer-checked:border-transparent peer-checked:bg-primary-400 peer-checked:[&_svg]:opacity-100 peer-checked:group-hover:border-transparent peer-checked:group-hover:bg-primary-300 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-400 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white'

interface Address {
  name: string
  street: string
  city: string
  state: string
  zip: string
  country: string
}

const emptyAddress: Address = {
  name: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  country: 'USA',
}

interface FormData {
  display_name: string
  email: string
  phone: string
  shipping_address: Address
  billing_address: Address
  marketing_consent: boolean
}

interface IsEditing {
  profile: boolean
  shipping: boolean
  billing: boolean
}

interface PasswordData {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

// Props for ProfileForm
interface ProfileFormProps {
  isEditing: boolean
  formData: {
    display_name: string
    email: string
    phone: string
    marketing_consent: boolean
  }
  setFormData: React.Dispatch<React.SetStateAction<FormData>>
  handleSaveProfile: () => Promise<void>
  saveLoading: boolean
  setIsEditing: React.Dispatch<React.SetStateAction<IsEditing>>
  profile: UseUserProfileReturn['profile']
  user: User | null
  phoneError?: string
  setPhoneError: React.Dispatch<React.SetStateAction<string>>
  updateProfile: UseUserProfileReturn['updateProfile']
}

const ProfileForm: React.FC<ProfileFormProps> = ({
  isEditing,
  formData,
  setFormData,
  handleSaveProfile,
  saveLoading,
  setIsEditing,
  profile,
  user,
  phoneError,
  setPhoneError,
  updateProfile,
}) => (
  <div className={`${secondaryCardClass} p-8`}>
    <div className="mb-6 border-b border-[rgba(36,34,30,0.06)] pb-6">
      <h2 className="font-sans text-base font-semibold text-ink-700">
        {profile?.display_name || user?.email?.split('@')[0] || 'User'}
      </h2>
    </div>

    {isEditing ? (
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault()
          handleSaveProfile()
        }}
      >
        <div>
          <label htmlFor="display_name" className={secondaryLabelClass}>
            Full Name
          </label>
          <input
            id="display_name"
            type="text"
            value={formData.display_name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, display_name: e.target.value }))
            }
            placeholder="Enter your full name"
            className={secondaryTextInputClass}
          />
        </div>

        <div>
          <label htmlFor="email" className={secondaryLabelClass}>
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
            placeholder="Enter your email"
            className={secondaryTextInputClass}
          />
        </div>

        <div>
          <label htmlFor="phone" className={secondaryLabelClass}>
            Phone Number
          </label>
          <input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => {
              const formattedValue = formatPhoneNumber(e.target.value)
              setFormData((prev) => ({ ...prev, phone: formattedValue }))
            }}
            placeholder="+1 (555) 123-4567"
            className={secondaryTextInputClass}
          />
          {phoneError && (
            <p className="text-xs text-red-600 mt-1.5">{phoneError}</p>
          )}
        </div>

        <div>
          <label className="group flex cursor-pointer items-start gap-3">
            <input
              id="profile-form-marketing-consent"
              type="checkbox"
              checked={formData.marketing_consent}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  marketing_consent: e.target.checked,
                }))
              }
              className={marketingConsentControlClass}
            />
            <span className={marketingConsentBoxClass} aria-hidden="true">
              <Check size={10} weight="bold" className="text-white" />
            </span>
            <span className="block font-sans text-fs-300 text-ink-700">
              Send me occasional notes
            </span>
          </label>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="submit"
            variant="primary"
            disabled={saveLoading}
            className="rounded-xl py-3 px-6"
          >
            {saveLoading ? 'Saving...' : 'Save Changes'}
          </Button>
          <button
            type="button"
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                display_name: profile?.display_name || '',
                email: profile?.email || '',
                phone: profile?.phone || '',
                marketing_consent: getMarketingConsent(profile?.preferences),
              }))
              setPhoneError('')
              setIsEditing((prev) => ({ ...prev, profile: false }))
            }}
            className="text-sm text-primary-500 hover:text-primary-600 transition-colors px-4"
          >
            Cancel
          </button>
        </div>
      </form>
    ) : (
      <div className="space-y-3">
        <div className="flex justify-between items-start">
          <div>
            <p className={`${kickerLabelClass} mb-1.5`}>Full Name</p>
            <p className="text-fs-400 text-ink-700">
              {profile?.display_name || (
                <span className="italic text-ink-400">Not set</span>
              )}
            </p>
          </div>
        </div>

        <div>
          <p className={`${kickerLabelClass} mb-1.5`}>Email Address</p>
          <p className="text-fs-400 text-ink-700">
            {profile?.email || (
              <span className="italic text-ink-400">Not set</span>
            )}
          </p>
        </div>

        <div>
          <p className={`${kickerLabelClass} mb-1.5`}>Phone Number</p>
          <p className="text-fs-400 text-ink-700">
            {profile?.phone || (
              <span className="italic text-ink-400">Not set</span>
            )}
          </p>
        </div>

        <div>
          <label className="group flex cursor-pointer items-start gap-3">
            <input
              id="profile-marketing-consent"
              type="checkbox"
              checked={formData.marketing_consent}
              onChange={(e) => {
                const newValue = e.target.checked
                setFormData((prev) => ({
                  ...prev,
                  marketing_consent: newValue,
                }))
                if (profile) {
                  updateProfile({
                    preferences: {
                      ...profile?.preferences,
                      marketing_consent: newValue,
                    },
                  }).then((result) => {
                    if (result.error) {
                      setFormData((prev) => ({
                        ...prev,
                        marketing_consent: !newValue,
                      }))
                    }
                  })
                }
              }}
              className={marketingConsentControlClass}
            />
            <span className={marketingConsentBoxClass} aria-hidden="true">
              <Check size={10} weight="bold" className="text-white" />
            </span>
            <span className="block font-sans text-fs-300 text-ink-700">
              Send me occasional notes
            </span>
          </label>
        </div>

        <div className="pt-2">
          <button
            type="button"
            onClick={() => setIsEditing((prev) => ({ ...prev, profile: true }))}
            className="serif-link-subtle inline-flex items-center gap-1 font-serif text-fs-300 italic text-ink-700 no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            <span>Edit</span>
            <span className="serif-link-accent">profile</span>
            <span className="serif-link-arrow" aria-hidden="true">
              →
            </span>
          </button>
        </div>
      </div>
    )}
  </div>
)

// Props for PasswordForm
interface PasswordFormProps {
  passwordData: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
  }
  setPasswordData: React.Dispatch<React.SetStateAction<PasswordData>>
  handlePasswordChange: () => Promise<void>
  passwordError: string
  saveLoading: boolean
  passwordSuccess: boolean
}

const PasswordForm: React.FC<PasswordFormProps> = ({
  passwordData,
  setPasswordData,
  handlePasswordChange,
  passwordError,
  saveLoading,
  passwordSuccess,
}) => (
  <div className={`${secondaryCardClass} p-8`}>
    <h2 className="mb-6 font-sans text-base font-semibold text-ink-700">
      Change Password
    </h2>

    {passwordSuccess ? (
      <div className="text-center py-6">
        <div className="w-12 h-12 bg-secondary-400 rounded-full mx-auto mb-4 flex items-center justify-center">
          <Check className="w-6 h-6 text-white" />
        </div>
        <h4 className="text-lg font-semibold text-ink-700 mb-1">
          Password updated
        </h4>
        <p className="text-sm text-ink-500">
          Your password has been changed successfully.
        </p>
      </div>
    ) : (
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault()
          handlePasswordChange()
        }}
      >
        <div>
          <label htmlFor="currentPassword" className={secondaryLabelClass}>
            Current Password
          </label>
          <input
            id="currentPassword"
            type="password"
            value={passwordData.currentPassword}
            onChange={(e) =>
              setPasswordData((prev) => ({
                ...prev,
                currentPassword: e.target.value,
              }))
            }
            placeholder="Enter current password"
            className={secondaryTextInputClass}
          />
          <p className={secondaryHelperTextClass}>
            For security, please verify your current password
          </p>
        </div>

        <div>
          <label htmlFor="newPassword" className={secondaryLabelClass}>
            New Password
          </label>
          <input
            id="newPassword"
            type="password"
            value={passwordData.newPassword}
            onChange={(e) =>
              setPasswordData((prev) => ({
                ...prev,
                newPassword: e.target.value,
              }))
            }
            placeholder="Enter new password"
            className={secondaryTextInputClass}
          />
        </div>

        <div>
          <label htmlFor="confirmPassword" className={secondaryLabelClass}>
            Confirm New Password
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={passwordData.confirmPassword}
            onChange={(e) =>
              setPasswordData((prev) => ({
                ...prev,
                confirmPassword: e.target.value,
              }))
            }
            placeholder="Confirm new password"
            className={secondaryTextInputClass}
          />
          {passwordError && (
            <p className="text-xs text-red-600 mt-1.5" role="alert">
              {passwordError}
            </p>
          )}
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            variant="primary"
            disabled={
              !passwordData.currentPassword ||
              !passwordData.newPassword ||
              !passwordData.confirmPassword ||
              saveLoading
            }
            className="px-6"
          >
            {saveLoading ? 'Updating...' : 'Update Password'}
          </Button>
        </div>
      </form>
    )}
  </div>
)

const AccountPage: React.FC = () => {
  const { user } = useAuth()
  const { profile, loading, error, updateProfile, updatePassword } =
    useUserProfile()
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'legal'>(
    'profile'
  )

  const [isEditing, setIsEditing] = useState<IsEditing>({
    profile: false,
    shipping: false,
    billing: false,
  })

  const [formData, setFormData] = useState<FormData>({
    display_name: '',
    email: '',
    phone: '',
    shipping_address: emptyAddress,
    billing_address: emptyAddress,
    marketing_consent: false,
  })

  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [phoneError, setPhoneError] = useState('')
  const [saveLoading, setSaveLoading] = useState(false)
  const isInitialized = React.useRef(false)

  // Update form data when profile loads - but only once to prevent input interference
  useEffect(() => {
    if (profile && !isInitialized.current) {
      setFormData({
        display_name: profile.display_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        shipping_address: (profile.shipping_address as Address) || emptyAddress,
        billing_address: (profile.billing_address as Address) || emptyAddress,
        marketing_consent: getMarketingConsent(profile.preferences),
      })
      isInitialized.current = true
    }
  }, [profile])

  const handleSaveProfile = async () => {
    setSaveLoading(true)
    setPhoneError('')

    try {
      if (formData.phone.trim()) {
        const validation = validatePhoneNumber(formData.phone)
        if (!validation.isValid) {
          setPhoneError(validation.error || 'Invalid phone number')
          setSaveLoading(false)
          return
        }
      }

      const result = await updateProfile({
        display_name: formData.display_name,
        email: formData.email,
        phone: formData.phone,
        preferences: {
          ...profile?.preferences,
          marketing_consent: formData.marketing_consent,
        },
      })

      if (result.error) {
        logError('AccountPage', 'Error saving profile:', result.error)
        showToast('Failed to save profile: ' + result.error, { type: 'error' })
        return
      }

      setIsEditing((prev) => ({ ...prev, profile: false }))
    } catch {
      showToast('Failed to save profile', { type: 'error' })
    } finally {
      setSaveLoading(false)
    }
  }

  const handlePasswordChange = async () => {
    setPasswordError('')
    setPasswordSuccess(false)

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long')
      return
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError('New password must be different from current password')
      return
    }

    setSaveLoading(true)
    try {
      const result = await updatePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      )

      if (result.error) {
        setPasswordError(result.error)
        return
      }

      setPasswordSuccess(true)
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      })
    } catch {
      setPasswordError('Failed to update password')
    } finally {
      setSaveLoading(false)
    }
  }

  // Error state
  if (error && !loading && !profile) {
    return (
      <AppLayout>
        <MetaTags
          title="Account | Printboard"
          description="Manage your Printboard account, details, and preferences."
          noindex={true}
        />
        <Box
          as="main"
          className="flex-1 flex items-center justify-center px-4 py-16"
        >
          <div className="w-full max-w-md text-center">
            <div className={`${secondaryCardClass} p-8`}>
              <h2 className="text-lg font-semibold text-ink-700 mb-2">
                Error loading profile
              </h2>
              <p className="text-sm text-ink-500 mb-6">{error}</p>
              <Button
                variant="primary"
                onClick={() => window.location.reload()}
                className="rounded-xl py-3 px-6"
              >
                Try Again
              </Button>
            </div>
          </div>
        </Box>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <MetaTags
        title="Account | Printboard"
        description="Manage your Printboard account, details, and preferences."
        noindex={true}
      />
      <PageContainer>
        <Box className="py-4 sm:py-8">
          <div className="w-full max-w-md mx-auto">
            <div className="mb-4 sm:mb-6">
              <h1 className={secondaryPageTitleClass}>Account</h1>
            </div>

            <div
              className="mb-6 flex gap-6 border-b border-[rgba(36,34,30,0.06)]"
              role="tablist"
              aria-label="Account sections"
            >
              <button
                id="profile-tab"
                type="button"
                onClick={() => {
                  setActiveTab('profile')
                  setPasswordSuccess(false)
                }}
                className={`border-b-2 pb-3 font-sans text-[13px] transition-colors duration-settle ${
                  activeTab === 'profile'
                    ? 'border-primary-400 font-medium text-ink-700'
                    : 'border-transparent text-ink-500 hover:text-ink-700'
                }`}
                role="tab"
                aria-selected={activeTab === 'profile'}
              >
                Profile
              </button>
              <button
                id="security-tab"
                type="button"
                onClick={() => setActiveTab('password')}
                className={`border-b-2 pb-3 font-sans text-[13px] transition-colors duration-settle ${
                  activeTab === 'password'
                    ? 'border-primary-400 font-medium text-ink-700'
                    : 'border-transparent text-ink-500 hover:text-ink-700'
                }`}
                role="tab"
                aria-selected={activeTab === 'password'}
              >
                Security
              </button>
              <button
                id="legal-tab"
                type="button"
                onClick={() => setActiveTab('legal')}
                className={`border-b-2 pb-3 font-sans text-[13px] transition-colors duration-settle ${
                  activeTab === 'legal'
                    ? 'border-primary-400 font-medium text-ink-700'
                    : 'border-transparent text-ink-500 hover:text-ink-700'
                }`}
                role="tab"
                aria-selected={activeTab === 'legal'}
              >
                Legal
              </button>
            </div>

            {activeTab === 'profile' && (
              <div role="tabpanel" aria-labelledby="profile-tab">
                {loading && !profile ? (
                  <div className={`${secondaryCardClass} p-8`}>
                    <div className="mb-6 border-b border-[rgba(36,34,30,0.06)] pb-6">
                      <h2 className="font-sans text-base font-semibold text-ink-700">
                        {user?.email?.split('@')[0] || 'Account'}
                      </h2>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className={`${kickerLabelClass} mb-1.5`}>
                          Full Name
                        </p>
                        <p className="text-fs-400 text-ink-400"> </p>
                      </div>
                      <div>
                        <p className={`${kickerLabelClass} mb-1.5`}>
                          Email Address
                        </p>
                        <p className="text-fs-400 text-ink-400"> </p>
                      </div>
                      <div>
                        <p className={`${kickerLabelClass} mb-1.5`}>
                          Phone Number
                        </p>
                        <p className="text-fs-400 text-ink-400"> </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <ProfileForm
                    isEditing={isEditing.profile}
                    formData={formData}
                    setFormData={setFormData}
                    handleSaveProfile={handleSaveProfile}
                    saveLoading={saveLoading}
                    setIsEditing={setIsEditing}
                    profile={profile}
                    user={user}
                    phoneError={phoneError}
                    setPhoneError={setPhoneError}
                    updateProfile={updateProfile}
                  />
                )}
              </div>
            )}

            {activeTab === 'password' && (
              <div role="tabpanel" aria-labelledby="security-tab">
                <PasswordForm
                  passwordData={passwordData}
                  setPasswordData={setPasswordData}
                  handlePasswordChange={handlePasswordChange}
                  passwordError={passwordError}
                  saveLoading={saveLoading}
                  passwordSuccess={passwordSuccess}
                />
              </div>
            )}

            {activeTab === 'legal' && (
              <div role="tabpanel" aria-labelledby="legal-tab">
                <div className={`${secondaryCardClass} p-6`}>
                  <div className="flex flex-col gap-2">
                    <Link to="/privacy" className={accountLegalLinkClass}>
                      Privacy Policy
                    </Link>
                    <Link to="/terms" className={accountLegalLinkClass}>
                      Terms of Service
                    </Link>
                    <Link to="/return-policy" className={accountLegalLinkClass}>
                      Refund Policy
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <p className="mt-12 text-center text-[13px] text-ink-400">
              Need help?{' '}
              <a href="/support" className={inlineLinkClass}>
                Contact support
              </a>
            </p>
          </div>
        </Box>
      </PageContainer>
      <ToastContainer />
    </AppLayout>
  )
}

export default AccountPage
